// CountyLandingPage.tsx — programmatic per-county SEO landing page at
// /foreclosures/:countyId (issue #965, epic #901 Wave 5-B).
//
// Building a real content page for all ~100 active NC counties is a
// doorway-page SEO risk: most counties have too few active leads to read as
// a real page. This component tiers itself by the SAME "active leads"
// figure /public already shows — `total_active` from
// GET /api/v1/public/county-stats/:id, which sums publicActiveStatuses
// (auction_scheduled, pending_confirmation, notice_of_default, notice_filed,
// in_rem, res_plus) in backend/internal/handlers/public_handler.go. No new
// backend endpoint or "active" definition was invented for this.
//
//   - total_active >= COUNTY_LANDING_LEAD_THRESHOLD (lib/publicCountyStats.ts,
//     a founder-adjustable constant): renders the full page, indexable.
//   - Below the bar, or the county doesn't exist: renders a short, honest
//     fallback and sets <meta name="robots" content="noindex"> (via useSeo)
//     so a stray link or a guessed URL never gets indexed as a thin page.
//   - A county that already has a hand-authored guide
//     (CountyTaxForeclosureGuide.tsx / MecklenburgTaxForeclosures.tsx)
//     redirects there instead of rendering a second, competing page for the
//     same county — see HAND_AUTHORED_GUIDE_URLS.
//
// Reuses existing conventions rather than inventing new ones: the
// .legal-page/.legal-content typography shell from CountyTaxForeclosureGuide,
// the .pd-bar/.pd-legend/.pd-ziptable stat-block classes from PublicDashboard,
// useSeo, and PublicPageHeader/Footer. No new CSS. The legal/statutory
// explanation is intentionally short and links out to the existing
// statute-cited /nc-tax-foreclosure-guide + /glossary pages rather than
// restating case law on every one of these programmatically-generated
// pages — both a duplicate-content deterrent (see also the
// programmatic-seo skill's "don't repeat the same paragraph across dozens
// of pages" guidance) and a legal-accuracy one: this component has no
// per-county source of real local color (courthouse names, geography) to
// draw on, unlike the hand-authored guides, so it doesn't invent any.

import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useSeo } from "../lib/useSeo";
import { apiClient } from "../lib/apiClient";
import { PublicPageHeader, PublicPageFooter } from "./PublicPageChrome";
import {
  CountyStats,
  bucketStatuses,
  leadMix,
  leadMixSummary,
  leadTypeRows,
  pct,
  qualifiesForLandingPage,
  HAND_AUTHORED_GUIDE_URLS,
} from "../lib/publicCountyStats";

const Shell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <>
    <PublicPageHeader />
    <div className="pd-page">
      <div className="pd-inner" id="main" tabIndex={-1}>{children}</div>
    </div>
    <PublicPageFooter />
  </>
);

const CountyLandingPage: React.FC = () => {
  const { countyId } = useParams<{ countyId: string }>();
  const navigate = useNavigate();
  const [stats, setStats] = useState<CountyStats | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [loading, setLoading] = useState(true);

  const guideRedirect = countyId ? HAND_AUTHORED_GUIDE_URLS[countyId] : undefined;

  // Redirect away immediately for counties with an existing hand-authored
  // guide — never render a competing page for the same county/search intent.
  useEffect(() => {
    if (guideRedirect) navigate(guideRedirect, { replace: true });
  }, [guideRedirect, navigate]);

  useEffect(() => {
    if (!countyId || guideRedirect) return;
    setLoading(true);
    setNotFound(false);
    setLoadError(false);
    apiClient
      .get<CountyStats>(`/public/county-stats/${encodeURIComponent(countyId)}`)
      .then(({ data }) => {
        setStats(data);
        setLoading(false);
      })
      .catch((e) => {
        if (e?.response?.status === 404) setNotFound(true);
        else setLoadError(true);
        setLoading(false);
      });
  }, [countyId, guideRedirect]);

  const qualifies = !!stats && qualifiesForLandingPage(stats.total_active);

  // The count is every active lead, and most are not foreclosures (Davidson:
  // 97% tax-delinquent), so the title and description name the mix instead of
  // calling the whole number "foreclosure leads".
  useSeo({
    title:
      qualifies && stats
        ? `${stats.county_name} Foreclosure and Distressed Property Leads, NC | Parcely`
        : "NC Foreclosure and Distressed Property Leads by County | Parcely",
    description:
      qualifies && stats
        ? `${stats.total_active.toLocaleString()} active leads in ${stats.county_name}, North Carolina: foreclosure auctions plus tax-delinquent, pre-foreclosure and other distressed properties. Refreshed twice daily.`
        : "NC foreclosure and distressed property lead data by county, refreshed twice daily. Free public data from Parcely.",
    // Every branch that isn't the fully-qualified page stays noindex: the
    // redirect target (guideRedirect) has its own real SEO page, and
    // loading/not-found/under-threshold are all states we never want a
    // crawler to index.
    noindex: !qualifies,
  });

  if (guideRedirect) return null;

  if (loading) {
    return (
      <Shell>
        <p className="pd-note">Loading…</p>
      </Shell>
    );
  }

  if (notFound) {
    return (
      <Shell>
        <h1 className="pd-title">County not found</h1>
        <p className="pd-note">
          We don&rsquo;t have this county in our data yet.{" "}
          <Link to="/public">See all counties we cover →</Link>
        </p>
      </Shell>
    );
  }

  if (loadError || !stats) {
    return (
      <Shell>
        <p className="pd-note">
          Could not load county data right now. <Link to="/public">See all counties →</Link>
        </p>
      </Shell>
    );
  }

  if (!qualifies) {
    return (
      <Shell>
        <h1 className="pd-title">{stats.county_name}, NC</h1>
        <p className="pd-note" style={{ marginTop: 10 }}>
          Parcely is tracking {stats.total_active} active lead
          {stats.total_active === 1 ? "" : "s"} in {stats.county_name} right now, not
          quite enough yet for a full county guide.
        </p>
        <p className="pd-note" style={{ marginTop: 10 }}>
          <Link to={`/public/${stats.county_id}`}>See the live {stats.county_name} numbers →</Link>
          {" · "}
          <Link to="/public">Browse every county we cover →</Link>
        </p>
      </Shell>
    );
  }

  const b = bucketStatuses(stats.status_breakdown);
  const tot = b.auction + b.pre;
  const leadRows = leadTypeRows(stats.lead_type_breakdown);
  const leadTot = leadRows.reduce((s, r) => s + r.count, 0);
  const mixSummary = leadMixSummary(leadMix(stats.lead_type_breakdown));

  return (
    <>
      <PublicPageHeader />
      <div className="legal-page" id="main" tabIndex={-1}>
        <div className="legal-content">
          <h1>Foreclosure and Distressed Property Leads in {stats.county_name}, NC</h1>
          <p className="legal-updated">
            Live data · updated 7:00 AM &amp; 7:00 PM ET from county and court records
          </p>

          {/* "Active leads", never "foreclosure leads": most of the total is
              tax-delinquent properties with no foreclosure filed, and the
              lead-type line below says so. The status bar is where a lead
              sits in the process; the lead-type section is what it is. */}
          <p>
            Parcely is currently tracking <strong>{stats.total_active.toLocaleString()} active
            leads</strong> in {stats.county_name}, North Carolina.{" "}
            {b.auction.toLocaleString()} {b.auction === 1 ? "is" : "are"} scheduled for foreclosure
            auction and {b.pre.toLocaleString()} {b.pre === 1 ? "has" : "have"} not reached an
            auction date yet.{mixSummary ? ` ${mixSummary}.` : ""} These numbers come straight from
            the same live feed that powers the map; nothing here is estimated.
          </p>

          <h2>Status breakdown</h2>
          <div className="pd-bar">
            <span style={{ width: pct(b.auction, tot), background: "var(--status-auction-scheduled)" }} />
            <span style={{ width: pct(b.pre, tot), background: "var(--status-nod)" }} />
          </div>
          <div className="pd-legend" style={{ marginBottom: 20 }}>
            <span className="pd-legend-item">
              <span className="pd-legend-dot" style={{ background: "var(--status-auction-scheduled)" }} />
              Auction scheduled <strong>{b.auction}</strong>
            </span>
            <span className="pd-legend-item">
              <span className="pd-legend-dot" style={{ background: "var(--status-nod)" }} />
              Not yet at auction <strong>{b.pre}</strong>
            </span>
          </div>

          {leadRows.length > 0 && (
            <>
              <h2>Lead type breakdown</h2>
              <p>
                Each property is counted once, under the most serious lead on it. A foreclosure
                filing counts ahead of an open code case, and both count ahead of being behind on
                property taxes.
              </p>
              <div className="pd-bar">
                {leadRows.map((r) => (
                  <span key={r.key} style={{ width: pct(r.count, leadTot), background: r.color }} />
                ))}
              </div>
              <div className="pd-legend" style={{ marginBottom: 20 }}>
                {leadRows.map((r) => (
                  <span key={r.key} className="pd-legend-item">
                    <span className="pd-legend-dot" style={{ background: r.color }} />
                    {r.label} <strong>{r.count.toLocaleString()}</strong>
                  </span>
                ))}
              </div>
              <ul>
                {leadRows.map((r) => (
                  <li key={r.key}>
                    <strong>{r.label}.</strong> {r.blurb}
                  </li>
                ))}
              </ul>
            </>
          )}

          {stats.top_zip_codes.length > 0 && (
            <>
              <h2>Top ZIP codes</h2>
              <table className="pd-ziptable">
                <thead>
                  <tr>
                    <th>ZIP</th>
                    <th className="pd-zip-right">Active</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.top_zip_codes.map((z) => (
                    <tr key={z.zip_code}>
                      <td>{z.zip_code}</td>
                      <td className="pd-zip-right">{z.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          <h2>How NC foreclosure sales work</h2>
          <p>
            Most NC foreclosure sales — tax and mortgage alike — end in a public auction followed
            by a statutory <strong>upset-bid window</strong>, where a higher bidder can still take
            over the sale before it becomes final. For the full statutory walkthrough (it&rsquo;s
            the same across every NC county), see our{" "}
            <Link to="/nc-tax-foreclosure-guide">NC tax foreclosure guide</Link> and the{" "}
            <Link to="/glossary/upset-bid">upset bid glossary entry</Link>.
          </p>

          <h2>How Parcely helps</h2>
          <p>
            Parcely pulls {stats.county_name}&rsquo;s public foreclosure, tax-delinquency and
            code-enforcement records into one place, refreshed twice daily. Browse active cases on
            a map, filter by lead type, and follow the bid history on a parcel instead of checking
            the courthouse file by hand.
          </p>
          <p>
            <Link
              to={`/public/${stats.county_id}`}
              className="btn-primary"
              style={{ display: "inline-block", textDecoration: "none", marginTop: "0.5rem" }}
            >
              See {stats.county_name} foreclosure data →
            </Link>
          </p>

          <hr className="legal-divider" />
          <p>
            <strong>This is general information, not legal or financial advice.</strong>{" "}
            Foreclosure rules, deposit amounts, and deadlines change, and every case has its own
            facts. Confirm anything you plan to act on with the {stats.county_name}{" "}
            courthouse and your own attorney before you bid.
          </p>
          <p className="legal-entity">
            <strong>Parcely LLC</strong>
            <br />
            Charlotte, NC
          </p>
        </div>
      </div>
      <PublicPageFooter />
    </>
  );
};

export default CountyLandingPage;
